import React, { useCallback, useState, useEffect } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { TextField } from '@mui/material';

interface AutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
    error?: boolean;
    helperText?: string;
    label?: string;
}

const CustomAutocomplete: React.FC<AutocompleteProps> = ({
    value,
    onChange,
    onPlaceSelect,
    error,
    helperText,
    label = 'Dirección'
}) => {
    const places = useMapsLibrary('places');
    const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | null>(null);

    useEffect(() => {
        if (places) {
            setSessionToken(new google.maps.places.AutocompleteSessionToken());
        }
    }, [places]);

    const handleInputChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;
            onChange(inputValue);

            if (
                inputValue.length > 2 &&
                google.maps.places.AutocompleteSuggestion &&
                google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions &&
                sessionToken
            ) {
                try {
                    const response = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                        input: inputValue,
                        sessionToken
                    });

                    setSuggestions(response.suggestions || []);
                    setShowSuggestions(true);
                } catch (error) {
                    console.error('Error fetching suggestions:', error);
                    setSuggestions([]);
                    setShowSuggestions(false);
                }
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        },
        [onChange, sessionToken]
    );

    const handleSuggestionClick = useCallback(
        (suggestion: google.maps.places.AutocompleteSuggestion) => {
            const displayText = suggestion.placePrediction?.text ? String(suggestion.placePrediction.text) : '';
            const placeId = suggestion.placePrediction?.placeId;
            onChange(displayText);
            setShowSuggestions(false);

            if (places && placeId && sessionToken) {
                const service = new places.PlacesService(document.createElement('div'));
                service.getDetails(
                    {
                        placeId,
                        sessionToken,
                        fields: ['formatted_address', 'place_id', 'geometry', 'name', 'address_components']
                    },
                    (place, status) => {
                        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
                            onPlaceSelect(place);
                            setSessionToken(new google.maps.places.AutocompleteSessionToken());
                        }
                    }
                );
            }
        },
        [onChange, onPlaceSelect, places, sessionToken]
    );

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <TextField
                fullWidth
                required
                label={label}
                variant="outlined"
                value={value}
                onChange={handleInputChange}
                onFocus={() => value.length > 2 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                error={error}
                helperText={helperText}
            />
            {showSuggestions && suggestions.length > 0 && (
                <ul
                    style={{
                        position: 'absolute',
                        zIndex: 1000,
                        width: '100%',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        marginTop: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        padding: 0,
                        listStyle: 'none',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                >
                    {suggestions.map((s, index) => (
                        <li
                            key={index}
                            style={{
                                padding: '8px 16px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee'
                            }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSuggestionClick(s);
                            }}
                        >
                            {s.placePrediction?.text?.toString()}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CustomAutocomplete;
